import { useState, useEffect, useRef } from "react";
import {
  FileText, Plus, X, Trash, FilePdf, Image, UploadSimple, PencilSimple, CalendarBlank,
} from "@phosphor-icons/react";
import { useAdminAuth } from "@/contexts/AdminAuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminReports({
  title = "Reports",
  endpoint = "reports",
  includeCategory = true,
  itemLabel = "Report",
  addButtonLabel = "Add Report",
  emptyLabel = "No reports yet",
}) {
  const { admin } = useAdminAuth();
  const canDelete = admin?.allow_delete !== false;
  const apiBase = `${BACKEND_URL}/api/${endpoint}`;
  const [reports, setReports] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("add"); // "add" | "edit"
  const [editReport, setEditReport] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const fileRef = useRef();

  const [form, setForm] = useState({
    title: "",
    category: "",
    year: "",
    publisher: "",
    description: "",
    file: null,
  });
  const [filePreview, setFilePreview] = useState(null);
  const [error, setError] = useState("");

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setForm((f) => ({ ...f, file }));
    if (file.type.startsWith("image/")) {
      setFilePreview({ type: "image", url: URL.createObjectURL(file), name: file.name });
    } else {
      setFilePreview({ type: "pdf", url: null, name: file.name });
    }
  };

  const resetForm = () => {
    setForm({ title: "", category: "", year: "", publisher: "", description: "", file: null });
    setFilePreview(null);
    setError("");
    if (fileRef.current) fileRef.current.value = "";
  };

  const openAddDrawer = () => {
    resetForm();
    setDrawerMode("add");
    setEditReport(null);
    setDrawerOpen(true);
  };

  const openEditDrawer = (report) => {
    setDrawerMode("edit");
    setEditReport(report);
    setForm({
      title: report.title,
      category: report.category || "",
      year: report.year,
      publisher: report.publisher,
      description: report.description,
      file: null,
    });
    setFilePreview(null);
    setError("");
    if (fileRef.current) fileRef.current.value = "";
    setDrawerOpen(true);
  };

  const fetchReports = async () => {
    try {
      const res = await fetch(apiBase);
      const data = await res.json();
      setReports(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    if (!includeCategory) {
      setCategories([]);
      return;
    }
    try {
      const res = await fetch(`${BACKEND_URL}/api/report-categories`);
      const data = await res.json();
      setCategories(Array.isArray(data) ? data : []);
    } catch {
      setCategories([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (drawerMode === "add" && !form.file) {
      setError("Please upload a PDF or image.");
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      if (drawerMode === "add") {
        const fd = new FormData();
        fd.append("title", form.title);
        if (includeCategory) fd.append("category", form.category);
        fd.append("year", form.year);
        fd.append("publisher", form.publisher);
        fd.append("description", form.description);
        fd.append("file", form.file);
        const res = await fetch(apiBase, { method: "POST", body: fd });
        if (!res.ok) throw new Error("Upload failed");
      } else {
        // Edit: send only changed text fields as JSON
        const body = {
          title: form.title,
          year: form.year,
          publisher: form.publisher,
          description: form.description,
        };
        if (includeCategory) body.category = form.category;
        const res = await fetch(`${apiBase}/${editReport.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error("Update failed");
      }
      await fetchReports();
      resetForm();
      setDrawerOpen(false);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    setDeleteId(id);
    try {
      await fetch(`${apiBase}/${id}`, { method: "DELETE" });
      setReports((r) => r.filter((x) => x.id !== id));
    } finally {
      setDeleteId(null);
    }
  };

  const inputCls = "w-full px-3.5 py-2.5 rounded-lg border text-sm outline-none transition-colors";
  const inputStyle = { borderColor: "rgba(26,92,55,0.2)", backgroundColor: "#F7F5F0", color: "#1C211F" };
  const labelCls = "block text-xs font-medium mb-1.5 uppercase tracking-wider";

  useEffect(() => {
    let active = true;

    setLoading(true);
    setReports([]);
    setCategories([]);
    setDrawerOpen(false);
    setEditReport(null);
    setError("");

    const loadReports = async () => {
      try {
        const res = await fetch(apiBase);
        const data = await res.json();
        if (active) setReports(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
        if (active) setReports([]);
      }
    };

    const loadCategories = async () => {
      if (!includeCategory) return;
      try {
        const res = await fetch(`${BACKEND_URL}/api/report-categories`);
        const data = await res.json();
        if (active) setCategories(Array.isArray(data) ? data : []);
      } catch {
        if (active) setCategories([]);
      }
    };

    Promise.all([loadReports(), loadCategories()]).finally(() => {
      if (active) setLoading(false);
    });

    return () => { active = false; };
  }, [apiBase, includeCategory]);

  return (
    <div className="relative">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h3 className="font-heading text-xl font-medium" style={{ color: "#1C211F" }}>{title}</h3>
          <p className="text-sm mt-0.5" style={{ color: "#4A5450" }}>
            {reports.length} report{reports.length !== 1 ? "s" : ""} published
          </p>
        </div>
        <button
          onClick={openAddDrawer}
          className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
          style={{ backgroundColor: "#1A5C37", color: "#F7F5F0" }}
          onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#14482B")}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#1A5C37")}
          data-testid="add-report-btn"
        >
          <Plus size={16} weight="bold" />
          {addButtonLabel}
        </button>
      </div>

      {/* Reports List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: "#1A5C37", borderTopColor: "transparent" }} />
        </div>
      ) : reports.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: "rgba(26,92,55,0.08)" }}>
            <FileText size={28} weight="duotone" style={{ color: "#1A5C37" }} />
          </div>
          <p className="text-sm font-medium" style={{ color: "#1C211F" }}>{emptyLabel}</p>
          <p className="text-xs mt-1" style={{ color: "#4A5450" }}>Click &quot;{addButtonLabel}&quot; to publish your first one.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {reports.map((r) => (
            <div
              key={r.id}
              className="flex flex-col p-5 rounded-xl border"
              style={{ backgroundColor: "#fff", borderColor: "rgba(26,92,55,0.1)" }}
              data-testid={`report-item-${r.id}`}
            >
              {/* Top row: file type icon + action buttons */}
              <div className="flex items-center justify-between mb-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "rgba(26,92,55,0.08)" }}>
                  {r.file_type === "pdf"
                    ? <FilePdf size={19} weight="duotone" style={{ color: "#1A5C37" }} />
                    : <Image size={19} weight="duotone" style={{ color: "#1A5C37" }} />}
                </div>
                <div className="flex items-center gap-1">
                  <a
                    href={`${BACKEND_URL}${r.file_url}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs font-medium px-2.5 py-1.5 rounded-lg border transition-colors"
                    style={{ color: "#1A5C37", borderColor: "rgba(26,92,55,0.2)" }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(26,92,55,0.06)")}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                  >
                    View
                  </a>
                  <button
                    onClick={() => openEditDrawer(r)}
                    className="p-1.5 rounded-lg transition-colors"
                    style={{ color: "#1A5C37" }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(26,92,55,0.08)")}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                    data-testid={`edit-report-${r.id}`}
                  >
                    <PencilSimple size={15} weight="duotone" />
                  </button>
                  {canDelete && (
                    <button
                      onClick={() => handleDelete(r.id)}
                      disabled={deleteId === r.id}
                      className="p-1.5 rounded-lg transition-colors"
                      style={{ color: "#B85C38" }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(184,92,56,0.08)")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                      data-testid={`delete-report-${r.id}`}
                    >
                      <Trash size={15} weight="duotone" />
                    </button>
                  )}
                </div>
              </div>

              {/* Metadata chips */}
              <div className="flex flex-wrap items-center gap-2 mb-2">
                {includeCategory && (
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: "rgba(184,92,56,0.1)", color: "#B85C38" }}>
                    {r.category}
                  </span>
                )}
                <span className="text-xs" style={{ color: "#4A5450" }}>{r.year}</span>
              </div>

              {/* Title */}
              <p className="text-sm font-medium mb-0.5 line-clamp-2 leading-snug" style={{ color: "#1C211F" }}>{r.title}</p>

              {/* Publisher */}
              <p className="text-xs mb-2" style={{ color: "#4A5450" }}>{r.publisher}</p>

              {/* Description */}
              {r.description && (
                <p className="text-xs line-clamp-2 leading-relaxed mb-2" style={{ color: "#4A5450" }}>{r.description}</p>
              )}

              {/* Upload timestamp */}
              {r.created_at && (
                <p className="flex items-center gap-1 text-xs mt-auto pt-2" style={{ color: "#7A8A84", borderTop: "1px solid rgba(26,92,55,0.06)" }}>
                  <CalendarBlank size={11} weight="duotone" />
                  {new Date(r.created_at).toLocaleString("en-IN", {
                    day: "numeric", month: "short", year: "numeric",
                    hour: "2-digit", minute: "2-digit", hour12: true,
                  })}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Drawer Overlay */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-40"
          style={{ backgroundColor: "rgba(0,0,0,0.35)" }}
          onClick={() => setDrawerOpen(false)}
        />
      )}

      {/* Drawer Panel */}
      <div
        className={`fixed top-0 right-0 h-full z-50 flex flex-col transition-transform duration-300 ${
          drawerOpen ? "translate-x-0" : "translate-x-full"
        }`}
        style={{ width: "min(480px, 100vw)", backgroundColor: "#fff", boxShadow: "-8px 0 32px rgba(0,0,0,0.12)" }}
      >
        {/* Drawer Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0" style={{ borderColor: "rgba(26,92,55,0.1)" }}>
          <h3 className="font-heading text-lg font-medium" style={{ color: "#1C211F" }}>
            {drawerMode === "add" ? `Add ${itemLabel}` : `Edit ${itemLabel}`}
          </h3>
          <button
            onClick={() => setDrawerOpen(false)}
            className="p-1.5 rounded-lg transition-colors"
            style={{ color: "#4A5450" }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(26,92,55,0.08)")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        {/* Drawer Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-6 space-y-5">

          {/* File Upload — only for Add mode */}
          {drawerMode === "add" && (
            <div>
              <label className={labelCls} style={{ color: "#4A5450" }}>Upload PDF / Image</label>
              <div
                className="relative flex flex-col items-center justify-center gap-2 p-6 rounded-xl border-2 border-dashed cursor-pointer transition-colors"
                style={{ borderColor: filePreview ? "#1A5C37" : "rgba(26,92,55,0.25)", backgroundColor: "#F7F5F0" }}
                onClick={() => fileRef.current?.click()}
              >
                <input ref={fileRef} type="file" accept=".pdf,image/*" className="hidden" onChange={handleFileChange} />
                {filePreview ? (
                  filePreview.type === "image" ? (
                    <img src={filePreview.url} alt="preview" className="max-h-28 rounded-lg object-cover" />
                  ) : (
                    <div className="flex items-center gap-2">
                      <FilePdf size={28} weight="duotone" style={{ color: "#1A5C37" }} />
                      <span className="text-sm font-medium truncate max-w-[200px]" style={{ color: "#1C211F" }}>{filePreview.name}</span>
                    </div>
                  )
                ) : (
                  <>
                    <UploadSimple size={28} weight="duotone" style={{ color: "#1A5C37" }} />
                    <p className="text-sm" style={{ color: "#4A5450" }}>Click to upload PDF or image</p>
                  </>
                )}
                {filePreview && (
                  <button
                    type="button"
                    className="absolute top-2 right-2 p-1 rounded-full"
                    style={{ backgroundColor: "rgba(184,92,56,0.1)", color: "#B85C38" }}
                    onClick={(e) => { e.stopPropagation(); setFilePreview(null); setForm((f) => ({ ...f, file: null })); if (fileRef.current) fileRef.current.value = ""; }}
                  >
                    <X size={14} weight="bold" />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Title */}
          <div>
            <label className={labelCls} style={{ color: "#4A5450" }}>Title</label>
            <input
              required
              type="text"
              placeholder="Report title"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              className={inputCls}
              style={inputStyle}
              onFocus={(e) => (e.target.style.borderColor = "#1A5C37")}
              onBlur={(e) => (e.target.style.borderColor = "rgba(26,92,55,0.2)")}
            />
          </div>

          {includeCategory && (
            <div>
              <label className={labelCls} style={{ color: "#4A5450" }}>Category</label>
              <select
                required
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                className={inputCls}
                style={inputStyle}
                onFocus={(e) => (e.target.style.borderColor = "#1A5C37")}
                onBlur={(e) => (e.target.style.borderColor = "rgba(26,92,55,0.2)")}
                data-testid="report-category-select"
              >
                <option value="" disabled>Select a category</option>
                {categories.length === 0 && (
                  <option value="" disabled>No categories configured — add them in Master Data</option>
                )}
                {categories.map((c) => (
                  <option key={c.id} value={c.name}>{c.name}</option>
                ))}
              </select>
              {categories.length === 0 && (
                <p className="text-xs mt-1.5" style={{ color: "#B85C38" }}>
                  No categories yet. Go to <strong>Master Data</strong> to add report categories first.
                </p>
              )}
            </div>
          )}

          {/* Year */}
          <div>
            <label className={labelCls} style={{ color: "#4A5450" }}>Year</label>
            <input
              required
              type="text"
              placeholder="e.g. 2024"
              value={form.year}
              onChange={(e) => setForm((f) => ({ ...f, year: e.target.value }))}
              className={inputCls}
              style={inputStyle}
              onFocus={(e) => (e.target.style.borderColor = "#1A5C37")}
              onBlur={(e) => (e.target.style.borderColor = "rgba(26,92,55,0.2)")}
            />
          </div>

          {/* Publisher */}
          <div>
            <label className={labelCls} style={{ color: "#4A5450" }}>Publisher</label>
            <input
              required
              type="text"
              placeholder="Name of publisher or organisation"
              value={form.publisher}
              onChange={(e) => setForm((f) => ({ ...f, publisher: e.target.value }))}
              className={inputCls}
              style={inputStyle}
              onFocus={(e) => (e.target.style.borderColor = "#1A5C37")}
              onBlur={(e) => (e.target.style.borderColor = "rgba(26,92,55,0.2)")}
            />
          </div>

          {/* Description */}
          <div>
            <label className={labelCls} style={{ color: "#4A5450" }}>Description</label>
            <textarea
              required
              rows={4}
              placeholder="Brief description of the report"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              className={inputCls}
              style={{ ...inputStyle, resize: "vertical" }}
              onFocus={(e) => (e.target.style.borderColor = "#1A5C37")}
              onBlur={(e) => (e.target.style.borderColor = "rgba(26,92,55,0.2)")}
            />
          </div>

          {error && (
            <p className="text-xs px-3 py-2 rounded-lg" style={{ color: "#B85C38", backgroundColor: "rgba(184,92,56,0.08)" }}>
              {error}
            </p>
          )}
        </form>

        {/* Drawer Footer */}
        <div className="px-6 py-4 border-t flex gap-3 flex-shrink-0" style={{ borderColor: "rgba(26,92,55,0.1)" }}>
          <button
            type="button"
            onClick={() => setDrawerOpen(false)}
            className="flex-1 py-2.5 rounded-lg text-sm font-medium border transition-colors"
            style={{ borderColor: "rgba(26,92,55,0.2)", color: "#4A5450" }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 py-2.5 rounded-lg text-sm font-medium transition-colors"
            style={{ backgroundColor: submitting ? "#4a8c62" : "#1A5C37", color: "#F7F5F0", cursor: submitting ? "not-allowed" : "pointer" }}
            data-testid="submit-report-btn"
          >
            {submitting ? (drawerMode === "add" ? "Uploading..." : "Saving...") : (drawerMode === "add" ? "Publish Report" : "Save Changes")}
          </button>
        </div>
      </div>
    </div>
  );
}
